import { forwardRef } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs } from "@volca/ui/components/ui/tabs";
import { cn } from "@volca/ui/lib/utils";
import type {
  AiScriptSpreadsheetViewerHandle,
  AiScriptSpreadsheetViewerProps,
} from "./ai-script-spreadsheet-viewer-types";
import { EmptySheetState } from "./empty-sheet-state";
import { NoVisibleSheetsState } from "./no-visible-sheets-state";
import { SelectionSummaryControl } from "./selection-summary-control";
import { SheetGridArea } from "./sheet-grid-area";
import { SheetLoadingState } from "./sheet-loading-state";
import { SheetTabList } from "./sheet-tab-list";
import { SpreadsheetRegionReviewBanner } from "./spreadsheet-region-review-banner";
import { SpreadsheetRegionWorkbookReviewBanner } from "./spreadsheet-region-workbook-review-banner";
import { useSpreadsheetViewer } from "./use-spreadsheet-viewer";
import { ViewerFormulaBar } from "./viewer-formula-bar";
import { ViewerOverlays } from "./viewer-overlays";
import { ViewerRowMenu } from "./viewer-row-menu";
import { WorkbookLoadError } from "./workbook-load-error";

export type {
  AiScriptSpreadsheetViewerHandle,
  AiScriptSpreadsheetViewerProps,
} from "./ai-script-spreadsheet-viewer-types";

export const AiScriptSpreadsheetViewer = forwardRef<
  AiScriptSpreadsheetViewerHandle,
  AiScriptSpreadsheetViewerProps
>(function AiScriptSpreadsheetViewer(props, ref) {
  const {
    businessId,
    runId,
    fillHeight = false,
    onAskAiAboutCell,
    onViewEditedCell,
    onNoteFromSelection,
    sidebarOpen = false,
    approvalStatus,
    onSheetApprovalChange,
    pendingSheetApproval,
  } = props;
  const viewer = useSpreadsheetViewer(props, ref);
  const {
    isLoading,
    error,
    viewerRef,
    prefetchSheet,
    selectionSummary,
    handleSheetChange,
    ui: { isMaximized, isSheetSwitcherOpen, setIsSheetSwitcherOpen },
    sheet: {
      spreadsheet,
      sheets,
      activeSheet,
      activeSheetValue,
      isTooNarrowForGrid,
      canMutateActiveSheet,
      activeRegionOverrideReviews,
      workbookRegionOverrideReviews,
    },
    mutations: { confirmSpreadsheetRegionOverride },
    regions: { confirmRegionOverride },
  } = viewer;

  if (isLoading) return <SheetLoadingState fillHeight={fillHeight} />;

  if (error) return <WorkbookLoadError error={error} />;

  if (!spreadsheet) {
    return (
      <EmptySheetState
        title="No Grid for this run"
        description="This run didn't produce a Grid."
        fillHeight={fillHeight}
      />
    );
  }

  if (sheets.length === 0) {
    return props.readOnlySource ? (
      <EmptySheetState
        title="No sheets to show"
        description="This workbook has no sheets."
        fillHeight={fillHeight}
      />
    ) : (
      <NoVisibleSheetsState businessId={businessId} fillHeight={fillHeight} />
    );
  }

  if (!activeSheet) {
    return (
      <EmptySheetState
        title="No sheet selected"
        description="Choose an available sheet to view its cells."
        fillHeight={fillHeight}
      />
    );
  }

  return (
    <Tabs
      ref={viewerRef}
      value={activeSheetValue}
      onValueChange={handleSheetChange}
      className={cn("w-full min-w-0", {
        "h-full": fillHeight && !isMaximized,
      })}
    >
      <Stack
        align="start"
        gap={0}
        // When maximized with a sidebar open, reserve its exact width on the
        // right so the two sit side-by-side (no overlap); otherwise the overlay
        // is full-width (right-0). Inline style keeps the reserved width tied to
        // the single sidebar-width source. `width: auto` is required so the
        // left/right pair actually sizes the box — the `fullWidth` (w-full)
        // class would otherwise force width:100% and make `right` a no-op, so
        // the grid would stay full width and hide its right columns behind the
        // sidebar.
        style={
          isMaximized && sidebarOpen
            ? { right: "var(--simple-sidebar-width)", width: "auto" }
            : undefined
        }
        className={cn("w-full", "min-w-0 overflow-hidden bg-surface-raised", {
          // z-40: over the app, under the popups opened from it (z-50).
          "fixed top-0 bottom-0 left-0 z-40 h-screen rounded-none": isMaximized,
          "right-0": isMaximized && !sidebarOpen,
          "rounded-xl border border-line": !isMaximized,
          "h-full": fillHeight && !isMaximized,
        })}
      >
        {!isTooNarrowForGrid && (
          <ViewerFormulaBar
            viewer={viewer}
            onViewEditedCell={onViewEditedCell}
            onSheetApprovalChange={onSheetApprovalChange}
            pendingSheetApproval={pendingSheetApproval}
          />
        )}

        <SpreadsheetRegionReviewBanner
          reviews={activeRegionOverrideReviews}
          canConfirm={canMutateActiveSheet}
          isConfirming={confirmSpreadsheetRegionOverride.isPending}
          onConfirm={confirmRegionOverride}
        />
        <SpreadsheetRegionWorkbookReviewBanner
          reviews={workbookRegionOverrideReviews}
        />

        <SheetGridArea
          viewer={viewer}
          activeSheet={activeSheet}
          fillHeight={fillHeight}
          canAskAiAboutCell={!!onAskAiAboutCell}
          canNoteFromSelection={!!onNoteFromSelection}
        />

        {!isTooNarrowForGrid && (
          <SheetTabList
            activeSheetId={activeSheetValue}
            isSheetSwitcherOpen={isSheetSwitcherOpen}
            sheets={sheets}
            onSheetSwitcherOpenChange={setIsSheetSwitcherOpen}
            onSheetSelect={handleSheetChange}
            onSheetPrefetchIntent={prefetchSheet}
            approvalStatus={approvalStatus}
            rightAccessory={
              selectionSummary ? (
                <SelectionSummaryControl summary={selectionSummary} />
              ) : null
            }
          />
        )}

        <ViewerRowMenu
          viewer={viewer}
          onAskAiAboutCell={onAskAiAboutCell}
          onViewEditedCell={onViewEditedCell}
          onNoteFromSelection={onNoteFromSelection}
        />
        <ViewerOverlays viewer={viewer} businessId={businessId} runId={runId} />
      </Stack>
    </Tabs>
  );
});
